import type { User } from "oidc-client-ts";
import type React from "react";

import { getFeature } from "@asurion-core/expert-workspace-feature-flagging";
import { getUserManagerSettings } from "@expert/authentication";
import { useEffect, useRef, useState } from "react";
import { AuthProvider, hasAuthParams, useAuth } from "react-oidc-context";
import { useNavigate } from "react-router";

import { setIdentityAsTestUser } from "@/analytics";
import { GlobalError } from "@/common-ui";
import { WorkspaceLoader } from "@/common-ui/WorkspaceLoader";
import { env } from "@/config";
import { useLogger } from "@/sdk/logging/LoggerProvider";
import { USER_FRIENDLY_NAME_QS_KEY } from "@/shared-utils/constants";
import { endDynatraceAction, startDynatraceAction } from "@/utils/RealUserMonitoringClient";

import { ErrorText } from "./elements";

const LOGIN_REQUIRED_ERROR_TYPE = "Login_required";
// oidc-client-ts deletes a callback's state before validating it, so reopening any callback URL fails with this.
// A fresh sign-in writes new state and cannot fail the same way, so restarting on this error alone cannot loop.
const STALE_CALLBACK_MESSAGE = "No matching state found in storage";

interface AuthenticationProviderProps {
    children?: React.ReactNode;
}

interface AuthHandlerProps {
    children?: React.ReactNode;
}

function useOnSigninCallback() {
    const navigate = useNavigate();
    return (user?: User) => void navigate(user?.url_state ?? globalThis.location.pathname, { replace: true });
}

export function AuthenticationProvider({ children }: AuthenticationProviderProps) {
    const onSigninCallback = useOnSigninCallback();
    return (
        <AuthProvider
            {...getUserManagerSettings({
                oidcSsoUrl: env.oidcSsoUrl,
                oidcSsoClientId: env.oidcSsoClientId,
                oidcSsoAdditionalScope: env.oidcSsoAdditionalScope,
                employeeId: new URLSearchParams(window.location.search).get(USER_FRIENDLY_NAME_QS_KEY) ?? undefined,
                isDev: import.meta.env.VITE_ENV_TYPE === "development",
            })}
            onSigninCallback={onSigninCallback}
        >
            <AuthHandler>{children}</AuthHandler>
        </AuthProvider>
    );
}

function AuthHandler({ children }: AuthHandlerProps) {
    const logger = useLogger({ module: "AuthenticationProvider" });
    const auth = useAuth();
    const [checkingIfTestUser, setCheckingIfTestUser] = useState<boolean>(true);
    const loginTracked = useRef(false);

    useEffect(() => {
        if (!hasAuthParams() && !auth.isAuthenticated && !auth.isLoading && !auth.activeNavigator) {
            logger.debug({ auth }, "auth login redirect");
            startDynatraceAction("Login");
            void auth.signinRedirect({
                nonce: crypto.randomUUID(),
                url_state: `${globalThis.location.pathname}${globalThis.location.search}${globalThis.location.hash}`,
            });
        }
    }, [auth, logger]);

    // `auth` is a new object on every auth state change, but `events` and `signinRedirect` are stable,
    // so keying on them subscribes once instead of stacking a handler per state change.
    // react-oidc-context pre-binds `signinRedirect` to its UserManager, so destructuring it is safe.
    // oxlint-disable-next-line typescript/unbound-method
    const { events, signinRedirect } = auth;
    useEffect(() => {
        const handleAccessTokenExpiring = () => {
            logger.info("Token expiring soon...");
        };
        const handleSilentRenewError = (error: Error) => {
            logger.info({ error }, "Silent renew error, redirecting to login");
            void signinRedirect({
                nonce: crypto.randomUUID(),
            });
        };

        events.addAccessTokenExpiring(handleAccessTokenExpiring);
        events.addSilentRenewError(handleSilentRenewError);
        return () => {
            events.removeAccessTokenExpiring(handleAccessTokenExpiring);
            events.removeSilentRenewError(handleSilentRenewError);
        };
    }, [events, signinRedirect, logger]);

    const staleCallbackError =
        auth.error?.source === "signinCallback" && auth.error.message === STALE_CALLBACK_MESSAGE
            ? auth.error
            : undefined;
    // Once the effect below starts the redirect it strips the auth params, so `signinRedirect` being active
    // is what keeps the loader up until the page unloads.
    const isRecoveringFromStaleCallback =
        staleCallbackError !== undefined && (hasAuthParams() || auth.activeNavigator === "signinRedirect");
    useEffect(() => {
        if (!staleCallbackError || !hasAuthParams()) return;
        // Drop the spent code/state from this history entry so neither Back nor reload can replay it.
        globalThis.history.replaceState(globalThis.history.state, "", globalThis.location.pathname);
        logger.warn({ error: staleCallbackError }, "Sign-in callback URL was already used, restarting sign-in");
        void signinRedirect({ nonce: crypto.randomUUID() });
    }, [staleCallbackError, signinRedirect, logger]);

    useEffect(() => {
        if (auth.isLoading) {
            return;
        }

        const checkIfUserIsTestUser = async () => {
            const isTestUser = await getFeature<boolean>("EWP-isTestUser", {
                environment: env.mode,
                channel: env.channel,
                employeeId: auth.user?.profile.employeeID ?? auth.user?.profile.userPrincipalName ?? "",
            });

            logger.info(`(Worker): isTestUser: '${isTestUser}'`);
            if (isTestUser) {
                setIdentityAsTestUser();
            }

            setCheckingIfTestUser(false);
        };

        void checkIfUserIsTestUser();
    }, [auth.isLoading, auth.user, logger]);

    switch (auth.activeNavigator) {
        case "signinSilent":
            return <WorkspaceLoader msg="Signing you in..." />;
        case "signoutRedirect":
            return <WorkspaceLoader msg="Signing you out..." />;
        case undefined:
        case "signinRedirect":
        case "signinPopup":
        case "signoutPopup":
        case "signoutSilent":
        case "signinResourceOwnerCredentials":
            break;
    }

    if (auth.error) {
        if (isRecoveringFromStaleCallback) {
            return <WorkspaceLoader msg="Signing you in..." />;
        }

        if ((auth.error.innerError as Error & { error: string }).error === LOGIN_REQUIRED_ERROR_TYPE) {
            return <WorkspaceLoader msg="Signing you in..." />;
        }

        if (!loginTracked.current) {
            loginTracked.current = true;
            endDynatraceAction("Login", { error: auth.error.message });
        }
        return <GlobalError title="An error occurred while signing you in" />;
    }

    if (auth.isLoading) return <WorkspaceLoader msg="Signing you in..." />;
    if (checkingIfTestUser) return <WorkspaceLoader msg="Checking identity..." />;
    if (auth.isAuthenticated) {
        if (!loginTracked.current) {
            loginTracked.current = true;
            endDynatraceAction("Login");
        }
        return <>{children} </>;
    }
    return <ErrorText>Not Authenticated</ErrorText>;
}
